import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Target, Users, Zap, Calendar, Clock, Tag,
  GitBranch, Star, History, Flag, CalendarDays, PlayCircle, CheckCircle2,
  Play, Pause, RotateCcw, ChevronDown, Paperclip, Pencil, Trash2,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { StarRating } from '@/components/shared/StarRating';
import { taskService, type TaskDetail, type TimerLogEntry, type Attachment } from '@/services/task.service';
import { EditUnifiedTaskDialog } from './EditUnifiedTaskDialog';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const timerActionConfig: Record<string, { icon: typeof Play; label: string; className: string }> = {
  start:      { icon: Play,        label: 'Start',    className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
  pause:      { icon: Pause,       label: 'Pause',    className: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
  resume:     { icon: RotateCcw,   label: 'Resume',   className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  stop:       { icon: CheckCircle2, label: 'Finish',  className: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
  finish:     { icon: CheckCircle2, label: 'Finish',  className: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
  auto_start: { icon: Play,        label: 'Auto Start', className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
  auto_pause: { icon: Pause,       label: 'Auto Pause', className: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
};

const priorityColors: Record<string, string> = {
  low:      'bg-blue-500/10 text-blue-700',
  medium:   'bg-yellow-500/10 text-yellow-700',
  high:     'bg-orange-500/10 text-orange-700',
  critical: 'bg-red-500/10 text-red-700',
};

const taskTypeConfig: Record<string, { icon: typeof Target; label: string; color: string }> = {
  goal:    { icon: Target, label: 'Goal Task',    color: 'text-blue-600' },
  general: { icon: Users,  label: 'General Task', color: 'text-green-600' },
  sprint:  { icon: Zap,    label: 'Sprint Task',  color: 'text-purple-600' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatTime(seconds: number) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function Separator() {
  return <div className="border-t border-border" />;
}

// ---------------------------------------------------------------------------
// Attachments Dialog (nested popup)
// ---------------------------------------------------------------------------
function AttachmentsDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  canUpload,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  entityType: string;
  entityId: string;
  canUpload: boolean;
}) {
  const { data: attachments, refetch } = useQuery({
    queryKey: ['attachments', entityType, entityId],
    queryFn: () => taskService.listAttachments(entityId).then((r) => r.data.data),
    enabled: open,
  });

  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await taskService.uploadAttachment(entityType, entityId, file);
      refetch();
    } catch { /* ignore */ }
    setUploading(false);
    e.target.value = '';
  }

  async function handleAddUrl() {
    if (!urlInput.trim()) return;
    try {
      await taskService.addUrlAttachment(entityType, entityId, urlInput.trim());
      setUrlInput('');
      refetch();
    } catch { /* ignore */ }
  }

  async function handleDelete(attId: string) {
    try {
      await taskService.deleteAttachment(attId);
      refetch();
    } catch { /* ignore */ }
  }

  async function handleDownload(att: Attachment) {
    if (att.attachment_type === 'url' && att.url) {
      window.open(att.url, '_blank');
      return;
    }
    try {
      const resp = await taskService.downloadAttachment(att.id);
      const data = resp.data.data;
      if (data.type === 'redirect' && data.url) {
        window.open(data.url, '_blank');
      }
    } catch { /* ignore */ }
  }

  const list: Attachment[] = attachments ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Task Attachments</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Attachments</span>
          </div>

          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Paperclip className="h-10 w-10 mb-2 opacity-30" />
              <p className="text-sm">No attachments or links yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {list.map((att) => (
                <div key={att.id} className="flex items-center justify-between rounded-lg border p-2.5 text-sm">
                  <button
                    type="button"
                    className="flex items-center gap-2 min-w-0 truncate hover:text-primary"
                    onClick={() => handleDownload(att)}
                  >
                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{att.file_name || att.url || 'Attachment'}</span>
                  </button>
                  {canUpload && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive shrink-0"
                      onClick={() => handleDelete(att.id)}
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {canUpload && (
            <div className="space-y-2 border-t pt-3">
              <div className="flex gap-2">
                <label className="flex-1">
                  <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                  <Button variant="outline" size="sm" className="w-full" asChild disabled={uploading}>
                    <span>{uploading ? 'Uploading...' : 'Upload File'}</span>
                  </Button>
                </label>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add URL link..."
                  className="flex-1 rounded-md border px-3 py-1.5 text-sm"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddUrl()}
                />
                <Button variant="outline" size="sm" onClick={handleAddUrl} disabled={!urlInput.trim()}>
                  Add
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface UnifiedTaskDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string | null;
  /** Optional: override edit permission (e.g. column leader context). If omitted, computed from user/task. */
  canEditOverride?: boolean;
  /** Optional: override delete permission. If omitted, computed from user/task. */
  canDeleteOverride?: boolean;
  /** Called after a successful delete so parent can refresh lists. */
  onDeleted?: () => void;
  /** Called when Edit is clicked. Receives the full task. Parent should open EditUnifiedTaskDialog. */
  onEdit?: (task: TaskDetail) => void;
  /** Optional: sprint members to pass to edit dialog */
  members?: { id: string; name: string }[];
  /** Optional: sprint goals for edit dialog */
  sprintGoals?: { id: string; title: string }[];
  /** Optional: sprint columns for edit dialog */
  columns?: { id: string; name: string }[];
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export function UnifiedTaskDetailsDialog({
  open, onOpenChange, taskId,
  canEditOverride, canDeleteOverride, onDeleted, onEdit,
  members, sprintGoals, columns,
}: UnifiedTaskDetailsDialogProps) {
  const { user, isAdmin, activeWorkspaceRole } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showAttachments, setShowAttachments] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [timerHistoryOpen, setTimerHistoryOpen] = useState(false);
  const [hoursHistoryOpen, setHoursHistoryOpen] = useState(false);

  const { data: task, isLoading } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => taskService.get(taskId!).then((r) => r.data.data),
    enabled: open && !!taskId,
  });

  const { data: timerLogsData } = useQuery({
    queryKey: ['task-timer-logs', taskId],
    queryFn: () => taskService.timerLogs(taskId!, { limit: 50 }).then((r) => r.data.data),
    enabled: open && !!taskId,
  });

  const deleteMut = useMutation({
    mutationFn: () => taskService.delete(taskId!),
    onSuccess: () => {
      toast({ title: 'Task deleted' });
      setShowDeleteConfirm(false);
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['sprint-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['sprint'] });
      onDeleted?.();
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Delete failed', variant: 'destructive' }),
  });

  const timerLogs: TimerLogEntry[] = timerLogsData ?? [];

  // Compute permissions
  const isWorkspaceAdmin = isAdmin || activeWorkspaceRole === 'owner' || activeWorkspaceRole === 'admin';
  const isAssignee = !!user && !!task && user.id === task.assignee_id;
  const isCreator = !!user && !!task && user.id === task.creator_id;

  const canEdit = canEditOverride ?? (isAssignee || isCreator || isWorkspaceAdmin);
  const canDelete = canDeleteOverride ?? (isCreator || isWorkspaceAdmin);
  const canUpload = isAssignee || isCreator || isWorkspaceAdmin;

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0">
          {isLoading || !task ? (
            <>
              <DialogHeader className="p-6 pb-0">
                <DialogTitle><Skeleton className="h-6 w-48" /></DialogTitle>
              </DialogHeader>
              <div className="p-6 pt-4 space-y-4">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            </>
          ) : (
            <TaskDialogContent
              task={task}
              timerLogs={timerLogs}
              timerHistoryOpen={timerHistoryOpen}
              setTimerHistoryOpen={setTimerHistoryOpen}
              hoursHistoryOpen={hoursHistoryOpen}
              setHoursHistoryOpen={setHoursHistoryOpen}
              onShowAttachments={() => setShowAttachments(true)}
              canEdit={canEdit}
              canDelete={canDelete}
              onEditClick={() => {
                if (onEdit) {
                  onEdit(task);
                } else {
                  setShowEditDialog(true);
                }
              }}
              onDeleteClick={() => setShowDeleteConfirm(true)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Nested Attachments Dialog */}
      {task && (
        <AttachmentsDialog
          open={showAttachments}
          onOpenChange={setShowAttachments}
          entityType="task"
          entityId={task.id}
          canUpload={canUpload}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {task && (
        <ConfirmDeleteDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          taskTitle={task.title}
          isLoading={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate()}
        />
      )}

      {/* Edit Dialog (built-in fallback when parent doesn't provide onEdit) */}
      {task && showEditDialog && (
        <EditUnifiedTaskDialog
          open={showEditDialog}
          onOpenChange={(v) => {
            setShowEditDialog(v);
            if (!v) {
              // Refresh task data after edit
              queryClient.invalidateQueries({ queryKey: ['task', taskId] });
            }
          }}
          task={task}
          members={members}
          sprintGoals={sprintGoals}
          columns={columns}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inner Content (extracted to avoid re-render flicker)
// ---------------------------------------------------------------------------
function TaskDialogContent({
  task,
  timerLogs,
  timerHistoryOpen,
  setTimerHistoryOpen,
  hoursHistoryOpen,
  setHoursHistoryOpen,
  onShowAttachments,
  canEdit,
  canDelete,
  onEditClick,
  onDeleteClick,
}: {
  task: TaskDetail;
  timerLogs: TimerLogEntry[];
  timerHistoryOpen: boolean;
  setTimerHistoryOpen: (v: boolean) => void;
  hoursHistoryOpen: boolean;
  setHoursHistoryOpen: (v: boolean) => void;
  onShowAttachments: () => void;
  canEdit: boolean;
  canDelete: boolean;
  onEditClick: () => void;
  onDeleteClick: () => void;
}) {
  const typeConfig = taskTypeConfig[task.task_type] ?? taskTypeConfig.general;
  const TypeIcon = typeConfig.icon;

  const hoursProgress = task.estimated_hours
    ? Math.min((task.actual_hours / task.estimated_hours) * 100, 100)
    : 0;

  const getParentInfo = () => {
    if (task.task_type === 'goal' && task.goal)
      return { label: 'Goal', name: task.goal.title, refId: task.goal.reference_id };
    if (task.task_type === 'general' && task.team)
      return { label: 'Team', name: task.team.name, refId: task.team.reference_id };
    if (task.task_type === 'sprint' && task.sprint)
      return { label: 'Sprint', name: task.sprint.name, refId: task.sprint.reference_id };
    return null;
  };
  const parentInfo = getParentInfo();

  return (
    <>
      {/* Header */}
      <DialogHeader className="p-6 pb-0">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className={cn('flex items-center gap-1', typeConfig.color)}>
                <TypeIcon className="h-4 w-4" />
                <span className="text-xs font-medium">{typeConfig.label}</span>
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {task.reference_id}
              </Badge>
            </div>
            <DialogTitle className="text-xl">{task.title}</DialogTitle>
          </div>
          {/* Action Buttons */}
          {(canEdit || canDelete) && (
            <div className="flex items-center gap-1.5 shrink-0">
              {canEdit && (
                <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onEditClick}>
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={onDeleteClick}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogHeader>

      <ScrollArea className="max-h-[calc(85vh-100px)]">
        <div className="p-6 pt-4 space-y-6">
          {/* Status Row */}
          <div className="flex flex-wrap items-center gap-2">
            <PhaseStatusBadge status={task.phase_status} />
            <CalcStatusBadge status={task.calculated_status} />
            <Badge variant="outline" className={priorityColors[task.priority] ?? ''}>
              <Flag className="h-3 w-3 mr-1" />
              {task.priority}
            </Badge>
            <div className="ml-auto">
              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={onShowAttachments}>
                <Paperclip className="h-3.5 w-3.5" />
                Attachments
              </Button>
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <>
              <div>
                <h4 className="text-sm font-medium mb-2">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.description}</p>
              </div>
              <Separator />
            </>
          )}

          {/* Parent Entity */}
          {parentInfo && (
            <div className="flex items-center gap-3">
              <TypeIcon className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">{parentInfo.label}</p>
                <p className="text-sm font-medium">{parentInfo.name}</p>
              </div>
            </div>
          )}

          {/* Sprint Column / Stage */}
          {task.task_type === 'sprint' && task.column && (
            <div className="flex items-center gap-3">
              <Zap className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Stage</p>
                <p className="text-sm font-medium">{task.column.name}</p>
              </div>
            </div>
          )}

          <Separator />

          {/* Assignee & Assigned by */}
          <div className="grid gap-4 sm:grid-cols-2">
            {task.assignee && (
              <div className="flex items-center gap-3">
                <UserAvatar name={task.assignee.full_name} avatarUrl={task.assignee.avatar_url} size="sm" />
                <div>
                  <p className="text-xs text-muted-foreground">Assignee</p>
                  <p className="text-sm font-medium">{task.assignee.full_name}</p>
                </div>
              </div>
            )}
            {task.creator && (
              <div className="flex items-center gap-3">
                <UserAvatar name={task.creator.full_name} avatarUrl={task.creator.avatar_url} size="sm" />
                <div>
                  <p className="text-xs text-muted-foreground">Assigned by</p>
                  <p className="text-sm font-medium">{task.creator.full_name}</p>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Dates Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Dates</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {task.start_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Start Date</p>
                    <p className="text-sm font-medium">{format(new Date(task.start_date), 'MMM d, yyyy')}</p>
                  </div>
                </div>
              )}
              {task.end_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Due Date</p>
                    <p className="text-sm font-medium">{format(new Date(task.end_date), 'MMM d, yyyy')}</p>
                  </div>
                </div>
              )}
              {task.first_timer_start && (
                <div className="flex items-center gap-3">
                  <PlayCircle className="h-4 w-4 text-green-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Started At</p>
                    <p className="text-sm font-medium">
                      {format(new Date(task.first_timer_start), "MMM d, yyyy 'at' h:mm a")}
                    </p>
                  </div>
                </div>
              )}
              {task.completed_at && task.phase_status === 'done' && (
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Completed At</p>
                    <p className="text-sm font-medium">
                      {format(new Date(task.completed_at), "MMM d, yyyy 'at' h:mm a")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Time Tracking */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Time Tracking</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {Number(task.actual_hours ?? 0).toFixed(2)}h / {Number(task.estimated_hours ?? 0).toFixed(2)}h
              </span>
            </div>
            <Progress value={hoursProgress} className="h-2" />

            {/* Timer History Collapsible */}
            {timerLogs.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
                  onClick={() => setTimerHistoryOpen(!timerHistoryOpen)}
                >
                  <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', timerHistoryOpen && 'rotate-180')} />
                  <History className="h-4 w-4" />
                  Timer History ({timerLogs.length} entries)
                </button>
                {timerHistoryOpen && (
                  <div className="pt-3 space-y-2 max-h-48 overflow-y-auto">
                    {timerLogs.map((log) => {
                      const config = timerActionConfig[log.action];
                      const Icon = config?.icon || Clock;
                      return (
                        <div key={log.id} className="flex items-center justify-between p-2 rounded-lg border text-sm">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={cn('gap-1', config?.className)}>
                              <Icon className="h-3 w-3" />
                              {config?.label || log.action}
                            </Badge>
                            <span className="text-muted-foreground text-xs">
                              {format(new Date(log.created_at), "MMM d 'at' h:mm a")}
                            </span>
                          </div>
                          <span className="font-mono text-xs">
                            {formatTime(log.accumulated_seconds || 0)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Hours Edit (Estimation) History Collapsible */}
            {task.estimation_history && task.estimation_history.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
                  onClick={() => setHoursHistoryOpen(!hoursHistoryOpen)}
                >
                  <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', hoursHistoryOpen && 'rotate-180')} />
                  <History className="h-4 w-4" />
                  Hours Edit History ({task.estimation_history.length} entries)
                </button>
                {hoursHistoryOpen && (
                  <div className="pt-3 space-y-2 max-h-48 overflow-y-auto">
                    {task.estimation_history.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-2 rounded-lg border text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="gap-1 bg-blue-500/10 text-blue-500 border-blue-500/20">
                            Estimated
                          </Badge>
                          <span>
                            {entry.old_value ?? 0}h → {entry.new_value ?? 0}h
                          </span>
                          {entry.changed_by && (
                            <span className="text-muted-foreground text-xs">
                              by {entry.changed_by.full_name}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(entry.created_at), "MMM d 'at' h:mm a")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Evaluation Score */}
          {(() => {
            const totalEvaluators = task.evaluators?.length ?? 0;
            const hasScore = task.evaluation_score !== null && task.evaluation_score !== undefined;

            if (hasScore) {
              return (
                <>
                  <Separator />
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Evaluation Score</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <StarRating value={task.evaluation_score!} size={18} />
                      <span className="text-sm text-muted-foreground">
                        {task.evaluation_score!.toFixed(1)} / 5.0
                      </span>
                    </div>
                  </div>
                </>
              );
            }

            if (totalEvaluators > 0) {
              return (
                <>
                  <Separator />
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Evaluation Progress</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Awaiting evaluations from all {totalEvaluators} evaluator{totalEvaluators > 1 ? 's' : ''} before score is calculated.
                    </p>
                  </div>
                </>
              );
            }

            return null;
          })()}

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <>
              <Separator />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Tags</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {task.tags.map((tagItem) => (
                    <Badge
                      key={tagItem.id}
                      variant="secondary"
                      className="text-xs"
                      style={tagItem.color ? { borderColor: tagItem.color, color: tagItem.color, backgroundColor: `${tagItem.color}15` } : undefined}
                    >
                      {tagItem.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Sprint Goals */}
          {task.sprint_goals && task.sprint_goals.length > 0 && (
            <>
              <Separator />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Sprint Goals</span>
                </div>
                <div className="space-y-1">
                  {task.sprint_goals.map((sg) => (
                    <div key={sg.id} className="text-sm text-muted-foreground">
                      • {sg.title}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Dependencies */}
          {task.dependencies && task.dependencies.length > 0 && (
            <>
              <Separator />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <GitBranch className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Dependencies</span>
                </div>
                <div className="space-y-1">
                  {task.dependencies.map((dep) => (
                    <div key={dep.id} className="text-sm text-muted-foreground">
                      • {dep.depends_on?.title || 'Unknown task'}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Evaluators */}
          {task.evaluators && task.evaluators.length > 0 && (
            <>
              <Separator />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Evaluators</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {task.evaluators.map((ev) => (
                    <div key={ev.id} className="flex items-center gap-2 p-2 rounded-md border">
                      <UserAvatar name={ev.user.full_name} avatarUrl={ev.user.avatar_url} size="xs" />
                      <span className="text-sm">{ev.user.full_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Metadata Timestamps */}
          <Separator />
          <div className="text-xs text-muted-foreground space-y-1">
            <p>Created on {task.created_at ? format(new Date(task.created_at), "MMM d, yyyy 'at' h:mm a") : 'N/A'}</p>
            <p>Last updated: {task.updated_at ? format(new Date(task.updated_at), "MMM d, yyyy 'at' h:mm a") : 'N/A'}</p>
          </div>

        </div>
      </ScrollArea>
    </>
  );
}

// ---------------------------------------------------------------------------
// Badge helpers matching old system
// ---------------------------------------------------------------------------
function PhaseStatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string }> = {
    not_started: { bg: 'bg-gray-100', text: 'text-gray-700' },
    in_progress: { bg: 'bg-blue-100', text: 'text-blue-700' },
    done:        { bg: 'bg-green-100', text: 'text-green-700' },
  };
  const c = config[status] ?? config.not_started;
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
  return <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', c.bg, c.text)}>{label}</span>;
}

function CalcStatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string }> = {
    pending:        { bg: 'bg-gray-100', text: 'text-gray-600' },
    on_time:        { bg: 'bg-cyan-100', text: 'text-cyan-700' },
    late:           { bg: 'bg-red-100', text: 'text-red-700' },
    completed_late: { bg: 'bg-red-100', text: 'text-red-700' },
  };
  const c = config[status] ?? config.pending;
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
  return <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', c.bg, c.text)}>{label}</span>;
}

// ---------------------------------------------------------------------------
// Delete Confirmation Dialog
// ---------------------------------------------------------------------------
function ConfirmDeleteDialog({
  open,
  onOpenChange,
  taskTitle,
  isLoading,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  taskTitle: string;
  isLoading: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Delete Task</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete &ldquo;{taskTitle}&rdquo;? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
